'use client';
// Routes a scanned lot label that opened the Admin phone app to admin /qr/<lot id>, where the
// BL-2355 resolver sends each staff member on (BL-2416, ADR 064 — Native Shell § 6). Mounted once
// in the root layout; renders nothing.
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { LOT_QR_PATH, parseLotQrLink } from '@auctogon/core/isomorphic/native-app-links/lot-qr-url';

import { getNativeDeviceGateway } from '../../gateways/native-device';

/**
 * Listens for app-link opens through the native-device gateway and pushes this environment's
 * label links to `/qr/<lot id>`. Every other URL (another environment, the sign-in callback) is
 * ignored. In a browser the gateway never delivers anything.
 * @returns nothing (renders no UI).
 * @sideEffect Navigates the app when it is opened from a label link.
 */
export function AppLinkOpener(): null {
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    void getNativeDeviceGateway()
      .then((gateway) => {
        if (cancelled) return;
        unsubscribe = gateway.onAppLinkOpen((url) => {
          const lotId = parseLotQrLink({ url, bidderBaseUrl: process.env.NEXT_PUBLIC_BIDDER_BASE_URL });
          if (lotId) router.push(`${LOT_QR_PATH}/${lotId}`);
        });
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS). Capacitor forwards it to Xcode / logcat.
        console.error('[app-link] native-device gateway failed to load', err);
      });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [router]);

  return null;
}
